import { PortalHost } from "@gorhom/portal";
import React, { ComponentType, PropsWithChildren, useMemo } from "react";
import { OnI18nErrorFn } from "@lookiero/i18n-react";
import { MessagingRoot } from "@lookiero/messaging-react/bootstrap";
import { Locale } from "@lookiero/sty-psp-locale";
import {
  SentryEnvironment,
  sentryLogger,
  SentryLoggerFunctionArgs,
  sentryLoggerHOC,
} from "@lookiero/sty-psp-logging/sentry";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Customer } from "../../../../projection/customer/customer";
import { KameleoonEnvironment } from "../../../ab-testing/kameleoonEnvironment";
import { i18n } from "../../i18n/i18n";
import {
  NextBestActionScreen,
  OnNextBestActionCompletedFunction,
  OnNextBestActionNavigateToStyleProfileFunction,
  OnNextBestActionPressCta,
  OnNextBestActionReadyFunction,
} from "../../views/nextBestAction/NextBestActionScreen";
import { nextBestActionProviders } from "./NextBestActionProviders";

type NextBestActionRootHostComponent = ComponentType<PropsWithChildren>;

interface NextBestActionRootProps {
  readonly customer: Customer | undefined;
  readonly locale: Locale;
  readonly tradename: Tradename;
  readonly onReady: OnNextBestActionReadyFunction;
  readonly onCompleted: OnNextBestActionCompletedFunction;
  readonly onNavigateToStyleProfile: OnNextBestActionNavigateToStyleProfileFunction;
  readonly onPressCta: OnNextBestActionPressCta;
}

type NextBestActionRootComponent = ComponentType<NextBestActionRootProps>;

interface NextBestActionRootFunctionArgs {
  readonly Messaging: MessagingRoot;
  readonly development?: boolean;
  readonly sentry: () => SentryEnvironment;
  readonly kameleoon: () => KameleoonEnvironment;
  readonly apiUrl: () => string;
  readonly getAuthToken: () => Promise<string>;
  readonly androidInAppBrowser?: boolean;
}

interface NextBestActionRootComponents {
  readonly NextBestActionRootHost: NextBestActionRootHostComponent;
  readonly NextBestActionRoot: NextBestActionRootComponent;
}

interface NextBestActionRootFunction {
  (args: NextBestActionRootFunctionArgs): (args: SentryLoggerFunctionArgs) => NextBestActionRootComponents;
}

const nextBestActionRoot: NextBestActionRootFunction = ({
  Messaging,
  kameleoon: kameleoonConfig,
  sentry,
  development,
  apiUrl,
  getAuthToken,
  androidInAppBrowser,
}) => {
  const logger = sentryLogger(sentry);
  const kameleoon = kameleoonConfig();
  const onI18nError: OnI18nErrorFn = (error, fingerprint) =>
    development
      ? void 0
      : logger.captureException(error, fingerprint ? { captureContext: { fingerprint: [...fingerprint] } } : undefined);

  const NextBestActionRootHost: NextBestActionRootHostComponent = ({ children }) => (
    <Messaging>
      <PortalHost name="StyleProfile" />
      {children}
    </Messaging>
  );

  const NextBestActionRoot: NextBestActionRootComponent = ({
    customer,
    locale,
    tradename,
    onCompleted,
    onReady,
    onNavigateToStyleProfile,
    onPressCta,
  }) => {
    const Providers = useMemo(() => {
      return nextBestActionProviders({
        apiUrl,
        getAuthToken,
        I18n: i18n({ tradename }),
        kameleoon,
        onI18nError,
        androidInAppBrowser,
      });
    }, [tradename]);

    const NextBestActionProviders = useMemo(
      () =>
        ({ children }: PropsWithChildren) => (
          <Providers customer={customer as Customer} locale={locale} tradename={tradename}>
            {children}
          </Providers>
        ),
      [customer, locale, tradename, Providers],
    );

    return (
      <NextBestActionProviders>
        <NextBestActionScreen
          screenModalProviders={NextBestActionProviders}
          onCompleted={onCompleted}
          onNavigateToStyleProfile={onNavigateToStyleProfile}
          onPressCta={onPressCta}
          onReady={onReady}
        />
      </NextBestActionProviders>
    );
  };

  const hoc = sentryLoggerHOC({ logger });

  /**
   * It's important to not call sentryLoggerHOC each time this function gets called.
   * That's why its called outside this method.
   */
  return ({ customerId }) => ({
    NextBestActionRootHost: hoc({ customerId })(NextBestActionRootHost),
    NextBestActionRoot: hoc({ customerId })(NextBestActionRoot),
  });
};

export type { NextBestActionRootComponents, NextBestActionRootComponent, NextBestActionRootHostComponent };
export { nextBestActionRoot };
